import * as React from 'react'
import {
  usePlantSessionPaint,
  type IngestChangesetEntry
} from '@axilon-apps/domains-ontology-ui-state'
import { PropertyDiffTable } from '../../components/DiffTable'
import {
  changesetEntryToDiffPresentation,
  type ChangesetDiffPresentation
} from './session-property-changes-model'

export {
  changesetEntryToDiffPresentation,
  type ChangesetDiffPresentation
} from './session-property-changes-model'

function useSessionPropertyDiff(entityId: string): {
  presentation: ChangesetDiffPresentation | null
  op: IngestChangesetEntry['op'] | undefined
} {
  const { active, changes, markFor, relationshipMarkFor } = usePlantSessionPaint()
  const mark = markFor(entityId) ?? relationshipMarkFor(entityId)
  const entry = changes.get(entityId)
  const presentation = React.useMemo(
    () => (entry ? changesetEntryToDiffPresentation(entry) : null),
    [entry]
  )
  if (!active || !presentation || mark === undefined || mark === 'dimmed') {
    return { presentation: null, op: undefined }
  }
  return { presentation, op: entry?.op }
}

/**
 * Always-visible session property diffs for the current entity (or relationship
 * id). Honors paint filter via markFor / relationshipMarkFor — dimmed and
 * paint-off hide the block. Mount outside collapsed drawers.
 * Session-local source citations render in EntityProvenancePanel instead.
 */
export function SessionEntityPropertyChanges({
  entityId
}: {
  entityId: string
}): React.ReactElement | null {
  const { presentation, op } = useSessionPropertyDiff(entityId)
  if (!presentation) return null

  return (
    <section className="overflow-hidden rounded-lg border border-sess/40 bg-sess-bg/40">
      <div className="flex items-baseline justify-between gap-2 border-b border-sess/30 px-3 py-2">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          Session changes
        </h3>
        <span className="text-11 text-ink-4">
          {op === 'deleted' ? 'removed this session' : 'updated this session'}
        </span>
      </div>
      <div className="px-1 py-1">
        <PropertyDiffTable
          rows={presentation.rows}
          columns={presentation.columns}
          struck={presentation.struck}
          valueHeader="Removed"
        />
      </div>
    </section>
  )
}

/** Compact diff under a list/table row (child property nodes, expanded rels). */
export function SessionInlinePropertyChanges({
  entityId
}: {
  entityId: string
}): React.ReactElement | null {
  const { presentation } = useSessionPropertyDiff(entityId)
  if (!presentation) return null
  return (
    <div className="mt-1.5 overflow-hidden rounded-md border border-sess/30 bg-sess-bg/30">
      <PropertyDiffTable
        rows={presentation.rows}
        columns={presentation.columns}
        struck={presentation.struck}
        valueHeader="Removed"
      />
    </div>
  )
}
